import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { type BreakdownItem, BreakdownTable } from '@/components/breakdown-table'
import { useDateWindow } from '@/components/date-range-picker'
import { EngineFilterControl, parseEngineFilter } from '@/components/engine-filter'
import { SerpStar } from '@/components/serp-star'
import { useTrackedKeywords } from '@/lib/serp'
import {
  DEFAULT_TABLE,
  type EngineFilter,
  type TableState,
  useQueriesBreakdown,
} from '@/lib/site-data'
import {
  FilterInput,
  SectionHeader,
  SiteSectionBody,
  TableSkeleton,
  useSiteScope,
} from '../../-section'

export const Route = createFileRoute('/_app/$org/$host/keywords/_list/')({
  // In the url, so a link to the Bing half of this table opens on it.
  validateSearch: (search: Record<string, unknown>): { engine?: EngineFilter } => {
    const engine = parseEngineFilter(search.engine)
    return engine ? { engine } : {}
  },
  component: SiteKeywordsPage,
})

function SiteKeywordsPage() {
  const navigate = Route.useNavigate()
  const { org, host, range, site } = useSiteScope()
  const [table, setTable] = useState<TableState>(DEFAULT_TABLE)
  const { engine = 'all' } = Route.useSearch()
  const { data, isPending, isFetching } = useQueriesBreakdown(site, range, table, engine)
  useDateWindow(data?.range)

  // The whole tracked list in one request, which is also what the SERP page
  // reads: a starred column asked per row, or per page of rows, would be a
  // second source of the same truth for the two to disagree over.
  const tracked = useTrackedKeywords(site)
  const starred = new Set(
    tracked.data?.rows.flatMap((row) => (row.queryId === null ? [] : [row.queryId])) ?? [],
  )

  const items: BreakdownItem[] =
    data?.rows.map((row) => ({
      key: String(row.queryId),
      label: row.query,
      metrics: row,
    })) ?? []

  return (
    <>
      <SectionHeader
        title="Keywords"
        filter={
          <>
            <FilterInput
              placeholder="Filter keywords"
              value={table.search}
              onChange={(search) => setTable({ ...table, search, offset: 0 })}
            />
            <EngineFilterControl
              value={engine}
              engines={data?.engines}
              onChange={(next) => {
                // A different engine is a different list, not page three of the old one.
                setTable({ ...table, offset: 0 })
                navigate({
                  search: (prev) => ({ ...prev, engine: next === 'all' ? undefined : next }),
                  replace: true,
                })
              }}
            />
          </>
        }
      />

      <SiteSectionBody
        org={org}
        host={host}
        range={range}
        result={data}
        isPending={isPending}
        // A filter that matches nothing has to leave the filter on screen, so
        // the section only counts as empty when nothing is being filtered.
        rowCount={table.search ? 1 : (data?.total ?? 0)}
        skeleton={<TableSkeleton />}
      >
        <BreakdownTable
          dimensionLabel="Keyword"
          items={items}
          total={data?.total ?? 0}
          table={table}
          onTableChange={setTable}
          emptyLabel="No keywords match that filter."
          isFetching={isFetching}
          onRowSelect={(item) =>
            navigate({
              to: '/$org/$host/keywords/$queryId',
              params: { org, host, queryId: item.key },
              search: { range },
            })
          }
          renderTrailing={(item) => (
            <SerpStar
              site={site}
              host={host}
              queryId={Number(item.key)}
              keyword={item.label}
              tracked={starred.has(Number(item.key))}
            />
          )}
          renderLabel={(item) => (
            <Link
              to="/$org/$host/keywords/$queryId"
              params={{ org, host, queryId: item.key }}
              search={{ range }}
              // The row navigates too, so this one only has to exist for the
              // keyboard, the middle button and the status bar's preview.
              onClick={(event) => event.stopPropagation()}
              className="underline-offset-2 hover:underline"
            >
              {item.label}
            </Link>
          )}
        />
      </SiteSectionBody>
    </>
  )
}
